import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Store, Loader2, Check, X, ExternalLink, Sparkles, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]];

export default function VendorApplicationsPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data = [], isLoading } = useQuery({
    queryKey: ["vendor-apps", status],
    queryFn: () => api.get(`/admin/vendor-applications?status=${status}`).then((r) => r.data),
  });

  const act = useMutation({
    mutationFn: ({ id, action, reason }) => api.post(`/admin/vendor-applications/${id}/${action}`, { reason, origin: window.location.origin }),
    onSuccess: (_r, v) => { toast.success(v.action === "approve" ? "Approved — seller access granted" : "Application rejected"); qc.invalidateQueries({ queryKey: ["vendor-apps"] }); qc.invalidateQueries({ queryKey: ["admin-vendor-apps-count"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="admin-vendor-applications">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><Store size={22} className="text-[#144F75]" /> Vendor & Enhancer Applications</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Review members applying to sell (Vendor) or offer a practice (Enhancer). Approving grants seller access; Enhancers also get a practitioner profile seeded from their answers.</p>
      <div className="flex gap-2 mb-5">
        {STATUS.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`vendor-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${status === k ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{l}</button>
        ))}
      </div>
      {isLoading ? <div className="p-8 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /></div>
        : data.length === 0 ? <div className="eyw-card p-8 text-center text-[#4A5D6B]">No {status} applications.</div>
        : (
          <div className="space-y-3">
            {data.map((a) => (
              <div key={a.id} className="eyw-card p-5" data-testid={`vendor-app-${a.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-serif text-lg text-[#1A242B]">{a.business_name}</div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${a.applicant_type === "enhancer" ? "bg-[#9E5C7F]/15 text-[#9E5C7F]" : "bg-[#144F75]/10 text-[#144F75]"}`} data-testid={`vendor-type-${a.id}`}>
                        {a.applicant_type === "enhancer" ? <><Sparkles size={10} /> Enhancer</> : <><Store size={10} /> Vendor</>}
                      </span>
                    </div>
                    <div className="text-sm text-[#4A5D6B]">{a.user_name} · {a.user_email}</div>
                    {a.bio && <p className="text-sm text-[#4A5D6B] mt-2"><b className="text-[#1A242B]">Bio:</b> {a.bio}</p>}
                    {a.description && a.description !== a.bio && <p className="text-sm text-[#4A5D6B] mt-2">{a.description}</p>}
                    {a.credentials && <p className="text-sm text-[#4A5D6B] mt-2"><b className="text-[#1A242B]">Credentials:</b> {a.credentials}</p>}
                    {a.path_story && <p className="text-sm text-[#4A5D6B] mt-2"><b className="text-[#1A242B]">Their path:</b> {a.path_story}</p>}
                    {a.mission && <p className="text-sm text-[#4A5D6B] mt-2"><b className="text-[#1A242B]">Mission:</b> {a.mission}</p>}
                    {(a.categories || []).length > 0 && <div className="flex gap-1.5 mt-2 flex-wrap">{a.categories.map((c) => <span key={c} className="text-xs bg-[#F1EEE8] rounded-full px-2.5 py-0.5">{c}</span>)}</div>}
                    {(a.images || []).length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap" data-testid={`vendor-app-images-${a.id}`}>
                        {a.images.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg overflow-hidden border border-[#E5E0D8]">
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    {(a.certifications || []).length > 0 && (
                      <div className="flex flex-col gap-1 mt-3" data-testid={`vendor-app-certs-${a.id}`}>
                        {a.certifications.map((c, i) => (
                          <a key={i} href={c.url} target="_blank" rel="noreferrer" className="text-sm text-[#144F75] inline-flex items-center gap-1.5"><FileText size={13} /> {c.name || `Certification ${i + 1}`}</a>
                        ))}
                      </div>
                    )}
                    {(a.social_links && Object.values(a.social_links).some(Boolean)) && (
                      <div className="flex gap-3 mt-2 flex-wrap text-xs text-[#144F75]">
                        {Object.entries(a.social_links).filter(([, v]) => v).map(([k, v]) => (
                          <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="capitalize inline-flex items-center gap-1">{k} <ExternalLink size={10} /></a>
                        ))}
                      </div>
                    )}
                    {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="text-sm text-[#144F75] inline-flex items-center gap-1 mt-2">{a.website} <ExternalLink size={12} /></a>}
                    {a.reason && <p className="text-xs text-[#8FA89B] mt-2">Note: {a.reason}</p>}
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => act.mutate({ id: a.id, action: "approve" })} disabled={act.isPending} data-testid={`vendor-approve-${a.id}`} className="eyw-btn !py-2 text-sm !bg-[#5C8A72]"><Check size={15} /> Approve</button>
                      <button onClick={() => { const reason = window.prompt("Reason for rejection (optional):") ?? ""; act.mutate({ id: a.id, action: "reject", reason }); }} disabled={act.isPending} data-testid={`vendor-reject-${a.id}`} className="eyw-btn-ghost !py-2 text-sm !text-[#B45151]"><X size={15} /> Reject</button>
                    </div>
                  )}
                  {a.status !== "pending" && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${a.status === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#B45151]/15 text-[#B45151]"}`}>{a.status}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
